import React, { useState, useEffect } from 'react';
import { useSearchParams, Link } from 'react-router-dom';
import { useAuth } from '../contexts/AuthContext';
import { Header } from '../components/Header';
import { Button } from '../components/ui/button';
import { Input } from '../components/ui/input';
import { Label } from '../components/ui/label';
import { Textarea } from '../components/ui/textarea';
import { Card, CardContent, CardHeader, CardTitle } from '../components/ui/card';
import { Badge } from '../components/ui/badge';
import { Tabs, TabsContent, TabsList, TabsTrigger } from '../components/ui/tabs';
import { Dialog, DialogContent, DialogDescription, DialogHeader, DialogTitle, DialogTrigger } from '../components/ui/dialog';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '../components/ui/select';
import { Avatar, AvatarFallback, AvatarImage } from '../components/ui/avatar';
import { REGIONS, getRegionLabel } from '../lib/regions';
import { RegionTabBar } from '../components/RegionTabBar';
import axios from 'axios';
import { toast } from 'sonner';
import { 
  Search, 
  Plus, 
  Clock, 
  Users, 
  HandHeart, 
  MessageSquare,
  Mail,
  Phone,
  MessageCircle,
  Filter,
  Loader2,
  X
} from 'lucide-react';
import { Building2, User } from 'lucide-react';

const API_URL = process.env.REACT_APP_BACKEND_URL;

const DEFAULT_CATEGORIES = [
  'Technology', 'Education', 'Home & Garden', 'Health & Wellness',
  'Arts & Crafts', 'Music', 'Languages', 'Cooking', 'Transportation',
  'Childcare', 'Pet Care', 'Administrative', 'Legal', 'Financial', 'Other'
];

const CONTACT_PREFERENCES = [
  { value: 'email', label: 'Email', icon: Mail },
  { value: 'phone', label: 'Phone/SMS', icon: Phone },
  { value: 'whatsapp', label: 'WhatsApp', icon: MessageCircle },
  { value: 'signal', label: 'Signal', icon: MessageCircle }
];

export function CommunityBoardPage() {
  const { user } = useAuth();
  const [searchParams, setSearchParams] = useSearchParams();
  const [activeTab, setActiveTab] = useState(searchParams.get('tab') || 'offers');
  const [offers, setOffers] = useState([]);
  const [requests, setRequests] = useState([]);
  const [skillCategories, setSkillCategories] = useState(DEFAULT_CATEGORIES);
  const [loading, setLoading] = useState(true);
  const [searchQuery, setSearchQuery] = useState('');
  const [categoryFilter, setCategoryFilter] = useState('');
  const [regionFilter, setRegionFilter] = useState(searchParams.get('region') || 'all');
  const [accountTypeFilter, setAccountTypeFilter] = useState('all');
  const [selectedPost, setSelectedPost] = useState(null); // { kind: 'offer'|'request', data }
  const [showOfferDialog, setShowOfferDialog] = useState(searchParams.get('action') === 'new' && activeTab === 'offers');
  const [showRequestDialog, setShowRequestDialog] = useState(searchParams.get('action') === 'new' && activeTab === 'requests');
  const [submitting, setSubmitting] = useState(false);

  // Form states
  const [offerForm, setOfferForm] = useState({
    title: '',
    description: '',
    skill_category: '',
    estimated_hours: 1,
    contact_preference: 'email',
    contact_value: '',
    region: user?.home_region || 'city_wide'
  });

  const [requestForm, setRequestForm] = useState({
    title: '',
    description: '',
    skill_needed: '',
    estimated_hours: 1,
    urgency: 'medium',
    contact_preference: 'email',
    contact_value: '',
    region: user?.home_region || 'city_wide'
  });

  useEffect(() => {
    fetchData();
    axios.get(`${API_URL}/api/site-content`)
      .then(res => {
        if (res.data?.categories?.length) setSkillCategories(res.data.categories);
      })
      .catch(() => {});
  }, [regionFilter, accountTypeFilter]);

  const fetchData = async () => {
    setLoading(true);
    try {
      const params = new URLSearchParams();
      if (regionFilter && regionFilter !== 'all') params.set('region', regionFilter);
      if (accountTypeFilter && accountTypeFilter !== 'all') params.set('account_type', accountTypeFilter);
      const queryStr = params.toString() ? `?${params.toString()}` : '';
      const [offersRes, requestsRes] = await Promise.all([
        axios.get(`${API_URL}/api/offers${queryStr}`, { withCredentials: true }),
        axios.get(`${API_URL}/api/requests${queryStr}`, { withCredentials: true })
      ]);
      setOffers(offersRes.data);
      setRequests(requestsRes.data);
    } catch (err) {
      if (process.env.NODE_ENV !== 'production') console.error('Error fetching data:', err);
      toast.error('Failed to load community board');
    } finally {
      setLoading(false);
    }
  };

  const handleCreateOffer = async (e) => {
    e.preventDefault();
    setSubmitting(true);
    try {
      const response = await axios.post(`${API_URL}/api/offers`, offerForm, { withCredentials: true });
      setOffers([response.data, ...offers]);
      setShowOfferDialog(false);
      setOfferForm({ title: '', description: '', skill_category: '', estimated_hours: 1, contact_preference: 'email', contact_value: '', region: user?.home_region || 'city_wide' });
      toast.success('Skill offer posted successfully!');
    } catch (err) {
      toast.error(err.response?.data?.detail || 'Failed to create offer');
    } finally {
      setSubmitting(false);
    }
  };

  const handleCreateRequest = async (e) => {
    e.preventDefault();
    setSubmitting(true);
    try {
      const response = await axios.post(`${API_URL}/api/requests`, requestForm, { withCredentials: true });
      setRequests([response.data, ...requests]);
      setShowRequestDialog(false);
      setRequestForm({ title: '', description: '', skill_needed: '', estimated_hours: 1, urgency: 'medium', contact_preference: 'email', contact_value: '', region: user?.home_region || 'city_wide' });
      toast.success('Support request posted successfully!');
    } catch (err) {
      toast.error(err.response?.data?.detail || 'Failed to create request');
    } finally {
      setSubmitting(false);
    }
  };

  const handleDeleteOffer = async (offerId) => {
    try {
      await axios.delete(`${API_URL}/api/offers/${offerId}`, { withCredentials: true });
      setOffers(offers.filter(o => o.offer_id !== offerId));
      toast.success('Offer deleted');
    } catch (err) {
      toast.error('Failed to delete offer');
    }
  };

  const handleDeleteRequest = async (requestId) => {
    try {
      await axios.delete(`${API_URL}/api/requests/${requestId}`, { withCredentials: true });
      setRequests(requests.filter(r => r.request_id !== requestId));
      toast.success('Request deleted');
    } catch (err) {
      toast.error('Failed to delete request');
    }
  };

  const filteredOffers = offers.filter(offer => {
    const matchesSearch = offer.title.toLowerCase().includes(searchQuery.toLowerCase()) ||
                          offer.description.toLowerCase().includes(searchQuery.toLowerCase()) ||
                          offer.skill_category.toLowerCase().includes(searchQuery.toLowerCase());
    const matchesCategory = !categoryFilter || offer.skill_category === categoryFilter;
    return matchesSearch && matchesCategory;
  });

  const filteredRequests = requests.filter(request => {
    const matchesSearch = request.title.toLowerCase().includes(searchQuery.toLowerCase()) ||
                          request.description.toLowerCase().includes(searchQuery.toLowerCase()) ||
                          request.skill_needed.toLowerCase().includes(searchQuery.toLowerCase());
    const matchesCategory = !categoryFilter || request.skill_needed === categoryFilter;
    return matchesSearch && matchesCategory;
  });

  const getUrgencyBadge = (urgency) => {
    const styles = {
      low: 'bg-[#d1c7e9]/10 text-[#d1c7e9]',
      medium: 'bg-[#ffd703]/10 text-[#ffd703]',
      high: 'bg-[#ef4444]/10 text-[#ef4444]'
    };
    return styles[urgency] || styles.medium;
  };

  const getContactIcon = (preference) => {
    const pref = CONTACT_PREFERENCES.find(p => p.value === preference);
    return pref ? pref.icon : Mail;
  };

  return (
    <div className="min-h-screen">
      <Header />
      
      <main className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-8" data-testid="community-board">
        {/* Header */}
        <div className="flex flex-col sm:flex-row sm:items-center justify-between gap-4 mb-8">
          <div>
            <h1 className="text-2xl sm:text-3xl font-semibold text-[#090000] mb-2">
              Community Board
            </h1>
            <p className="text-[#090000/60]">
              Browse skill offers and support requests from community members
            </p>
          </div>
          <div className="flex gap-3">
            <Dialog open={showOfferDialog} onOpenChange={setShowOfferDialog}>
              <DialogTrigger asChild>
                <Button className="bg-[#ffd703] hover:bg-[#e6c203]" data-testid="offer-skill-btn">
                  <HandHeart className="w-4 h-4 mr-2" />
                  Offer Skill
                </Button>
              </DialogTrigger>
              <DialogContent className="sm:max-w-lg">
                <DialogHeader>
                  <DialogTitle>Offer a Skill</DialogTitle>
                  <DialogDescription>Share a skill you're willing to offer in exchange for time credits.</DialogDescription>
                </DialogHeader>
                <form onSubmit={handleCreateOffer} className="space-y-4 mt-4">
                  <div className="space-y-2">
                    <Label>Title</Label>
                    <Input
                      placeholder="e.g., Python Programming Tutoring"
                      value={offerForm.title}
                      onChange={(e) => setOfferForm({...offerForm, title: e.target.value})}
                      required
                      data-testid="offer-title"
                    />
                  </div>
                  <div className="space-y-2">
                    <Label>Description</Label>
                    <Textarea
                      placeholder="Describe what you can help with..."
                      value={offerForm.description}
                      onChange={(e) => setOfferForm({...offerForm, description: e.target.value})}
                      required
                      rows={3}
                      data-testid="offer-description"
                    />
                  </div>
                  <div className="grid grid-cols-2 gap-4">
                    <div className="space-y-2">
                      <Label>Category</Label>
                      <Select 
                        value={offerForm.skill_category} 
                        onValueChange={(v) => setOfferForm({...offerForm, skill_category: v})}
                      >
                        <SelectTrigger data-testid="offer-category">
                          <SelectValue placeholder="Select category" />
                        </SelectTrigger>
                        <SelectContent>
                          {skillCategories.map(cat => (
                            <SelectItem key={cat} value={cat}>{cat}</SelectItem>
                          ))}
                        </SelectContent>
                      </Select>
                    </div>
                    <div className="space-y-2">
                      <Label>Estimated Hours</Label>
                      <Input
                        type="number"
                        min="0.5"
                        step="0.5"
                        value={offerForm.estimated_hours}
                        onChange={(e) => setOfferForm({...offerForm, estimated_hours: parseFloat(e.target.value)})}
                        required
                        data-testid="offer-hours"
                      />
                    </div>
                  </div>
                  <div className="space-y-2">
                    <Label>Contact Preference</Label>
                    <Select 
                      value={offerForm.contact_preference} 
                      onValueChange={(v) => setOfferForm({...offerForm, contact_preference: v})}
                    >
                      <SelectTrigger data-testid="offer-contact">
                        <SelectValue />
                      </SelectTrigger>
                      <SelectContent>
                        {CONTACT_PREFERENCES.map(pref => (
                          <SelectItem key={pref.value} value={pref.value}>
                            <span className="flex items-center gap-2">
                              <pref.icon className="w-4 h-4" />
                              {pref.label}
                            </span>
                          </SelectItem>
                        ))}
                      </SelectContent>
                    </Select>
                  </div>
                  <div className="space-y-2">
                    <Label>Your {offerForm.contact_preference === 'email' ? 'Email' : offerForm.contact_preference === 'phone' ? 'Phone Number' : offerForm.contact_preference === 'whatsapp' ? 'WhatsApp Number' : 'Signal Handle'}</Label>
                    <Input
                      required
                      value={offerForm.contact_value}
                      onChange={(e) => setOfferForm({...offerForm, contact_value: e.target.value})}
                      placeholder={offerForm.contact_preference === 'email' ? 'you@example.com' : offerForm.contact_preference === 'phone' ? '+1-555-0100' : offerForm.contact_preference === 'whatsapp' ? '+1-555-0100' : '@yourhandle'}
                      data-testid="offer-contact-value"
                    />
                    <p className="text-xs text-black/60 font-sans">Shown when someone clicks your post.</p>
                  </div>
                  <div className="space-y-2">
                    <Label>Post to which dashboard?</Label>
                    <Select 
                      value={offerForm.region} 
                      onValueChange={(v) => setOfferForm({...offerForm, region: v})}
                    >
                      <SelectTrigger data-testid="offer-region">
                        <SelectValue />
                      </SelectTrigger>
                      <SelectContent>
                        {REGIONS.map(r => (
                          <SelectItem key={r.value} value={r.value}>{r.label}</SelectItem>
                        ))}
                      </SelectContent>
                    </Select>
                  </div>
                  <div className="flex justify-end gap-3 pt-4">
                    <Button type="button" variant="outline" onClick={() => setShowOfferDialog(false)}>
                      Cancel
                    </Button>
                    <Button type="submit" className="bg-[#ffd703] hover:bg-[#e6c203]" disabled={submitting} data-testid="offer-submit">
                      {submitting ? <Loader2 className="w-4 h-4 animate-spin mr-2" /> : null}
                      Post Offer
                    </Button>
                  </div>
                </form>
              </DialogContent>
            </Dialog>

            <Dialog open={showRequestDialog} onOpenChange={setShowRequestDialog}>
              <DialogTrigger asChild>
                <Button variant="outline" className="" data-testid="request-help-btn">
                  <MessageSquare className="w-4 h-4 mr-2" />
                  Request Help
                </Button>
              </DialogTrigger>
              <DialogContent className="sm:max-w-lg">
                <DialogHeader>
                  <DialogTitle>Request Support</DialogTitle>
                  <DialogDescription>Tell the community what kind of help you're looking for.</DialogDescription>
                </DialogHeader>
                <form onSubmit={handleCreateRequest} className="space-y-4 mt-4">
                  <div className="space-y-2">
                    <Label>Title</Label>
                    <Input
                      placeholder="e.g., Need help with website setup"
                      value={requestForm.title}
                      onChange={(e) => setRequestForm({...requestForm, title: e.target.value})}
                      required
                      data-testid="request-title"
                    />
                  </div>
                  <div className="space-y-2">
                    <Label>Description</Label>
                    <Textarea
                      placeholder="Describe what you need help with..."
                      value={requestForm.description}
                      onChange={(e) => setRequestForm({...requestForm, description: e.target.value})}
                      required
                      rows={3}
                      data-testid="request-description"
                    />
                  </div>
                  <div className="grid grid-cols-2 gap-4">
                    <div className="space-y-2">
                      <Label>Skill Needed</Label>
                      <Select 
                        value={requestForm.skill_needed} 
                        onValueChange={(v) => setRequestForm({...requestForm, skill_needed: v})}
                      >
                        <SelectTrigger data-testid="request-skill">
                          <SelectValue placeholder="Select skill" />
                        </SelectTrigger>
                        <SelectContent>
                          {skillCategories.map(cat => (
                            <SelectItem key={cat} value={cat}>{cat}</SelectItem>
                          ))}
                        </SelectContent>
                      </Select>
                    </div>
                    <div className="space-y-2">
                      <Label>Estimated Hours</Label>
                      <Input
                        type="number"
                        min="0.5"
                        step="0.5"
                        value={requestForm.estimated_hours}
                        onChange={(e) => setRequestForm({...requestForm, estimated_hours: parseFloat(e.target.value)})}
                        required
                        data-testid="request-hours"
                      />
                    </div>
                  </div>
                  <div className="space-y-2">
                    <Label>Urgency</Label>
                    <Select 
                      value={requestForm.urgency} 
                      onValueChange={(v) => setRequestForm({...requestForm, urgency: v})}
                    >
                      <SelectTrigger data-testid="request-urgency">
                        <SelectValue />
                      </SelectTrigger>
                      <SelectContent>
                        <SelectItem value="low">Low - No rush</SelectItem>
                        <SelectItem value="medium">Medium - Within a week</SelectItem>
                        <SelectItem value="high">High - Urgent</SelectItem>
                      </SelectContent>
                    </Select>
                  </div>
                  <div className="space-y-2">
                    <Label>How should helpers contact you?</Label>
                    <Select 
                      value={requestForm.contact_preference} 
                      onValueChange={(v) => setRequestForm({...requestForm, contact_preference: v})}
                    >
                      <SelectTrigger data-testid="request-contact-pref">
                        <SelectValue />
                      </SelectTrigger>
                      <SelectContent>
                        {CONTACT_PREFERENCES.map(pref => (
                          <SelectItem key={pref.value} value={pref.value}>
                            <span className="flex items-center gap-2">
                              <pref.icon className="w-4 h-4" />
                              {pref.label}
                            </span>
                          </SelectItem>
                        ))}
                      </SelectContent>
                    </Select>
                  </div>
                  <div className="space-y-2">
                    <Label>Your {requestForm.contact_preference === 'email' ? 'Email' : requestForm.contact_preference === 'phone' ? 'Phone Number' : requestForm.contact_preference === 'whatsapp' ? 'WhatsApp Number' : 'Signal Handle'}</Label>
                    <Input
                      required
                      value={requestForm.contact_value}
                      onChange={(e) => setRequestForm({...requestForm, contact_value: e.target.value})}
                      placeholder={requestForm.contact_preference === 'email' ? 'you@example.com' : requestForm.contact_preference === 'phone' ? '+1-555-0100' : requestForm.contact_preference === 'whatsapp' ? '+1-555-0100' : '@yourhandle'}
                      data-testid="request-contact-value"
                    />
                    <p className="text-xs text-black/60 font-sans">Shown when someone clicks your request.</p>
                  </div>
                  <div className="space-y-2">
                    <Label>Post to which dashboard?</Label>
                    <Select 
                      value={requestForm.region} 
                      onValueChange={(v) => setRequestForm({...requestForm, region: v})}
                    >
                      <SelectTrigger data-testid="request-region">
                        <SelectValue />
                      </SelectTrigger>
                      <SelectContent>
                        {REGIONS.map(r => (
                          <SelectItem key={r.value} value={r.value}>{r.label}</SelectItem>
                        ))}
                      </SelectContent>
                    </Select>
                  </div>
                  <div className="flex justify-end gap-3 pt-4">
                    <Button type="button" variant="outline" onClick={() => setShowRequestDialog(false)}>
                      Cancel
                    </Button>
                    <Button type="submit" className="bg-[#ffd703] hover:bg-[#e6c203]" disabled={submitting} data-testid="request-submit">
                      {submitting ? <Loader2 className="w-4 h-4 animate-spin mr-2" /> : null}
                      Post Request
                    </Button>
                  </div>
                </form>
              </DialogContent>
            </Dialog>
          </div>
        </div>

        <RegionTabBar value={regionFilter} onChange={setRegionFilter} />

        {/* Account type filter */}
        <div className="mb-6">
          <div className="flex items-center gap-2 mb-2">
            <span className="text-sm font-sans font-semibold text-black">Post type:</span>
            <span className="text-xs text-black/60 font-sans">{accountTypeFilter === 'all' ? 'Everyone' : accountTypeFilter === 'business' ? 'Businesses only' : 'Individuals only'}</span>
          </div>
          <div className="flex flex-wrap gap-2" data-testid="account-type-filter">
            {[
              { value: 'all', label: 'Everyone' },
              { value: 'individual', label: 'Individuals' },
              { value: 'business', label: 'Businesses' },
            ].map(opt => (
              <button
                key={opt.value}
                type="button"
                onClick={() => setAccountTypeFilter(opt.value)}
                className={`rounded-md px-3 py-1.5 border-2 border-black text-sm font-sans font-semibold transition-all ${accountTypeFilter === opt.value ? 'bg-[#ffd703] shadow-[3px_3px_0px_#000]' : 'bg-white hover:bg-[#fef9e7]'}`}
                data-testid={`account-type-filter-${opt.value}`}
              >
                {opt.label}
              </button>
            ))}
          </div>
        </div>

        {/* Search and Filters */}
        <div className="flex flex-col sm:flex-row gap-4 mb-6">
          <div className="relative flex-1">
            <Search className="absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4 text-[#090000/60]" />
            <Input
              placeholder="Search offers and requests..."
              value={searchQuery}
              onChange={(e) => setSearchQuery(e.target.value)}
              className="pl-10"
              data-testid="search-input"
            />
          </div>
          <div className="flex gap-3">
            <Select value={categoryFilter || "all"} onValueChange={(v) => setCategoryFilter(v === "all" ? "" : v)}>
              <SelectTrigger className="w-[180px]" data-testid="category-filter">
                <Filter className="w-4 h-4 mr-2" />
                <SelectValue placeholder="All Categories" />
              </SelectTrigger>
              <SelectContent>
                <SelectItem value="all">All Categories</SelectItem>
                {skillCategories.map(cat => (
                  <SelectItem key={cat} value={cat}>{cat}</SelectItem>
                ))}
              </SelectContent>
            </Select>
            {categoryFilter && (
              <Button 
                variant="ghost" 
                size="icon"
                onClick={() => setCategoryFilter('')}
                className="text-[#090000/60]"
              >
                <X className="w-4 h-4" />
              </Button>
            )}
          </div>
        </div>

        {/* Tabs */}
        <Tabs value={activeTab} onValueChange={setActiveTab}>
          <TabsList className="bg-[#c7dfe9] mb-6">
            <TabsTrigger value="offers" className="data-[state=active]:bg-white" data-testid="tab-offers">
              <HandHeart className="w-4 h-4 mr-2" />
              Skill Offers ({filteredOffers.length})
            </TabsTrigger>
            <TabsTrigger value="requests" className="data-[state=active]:bg-white" data-testid="tab-requests">
              <MessageSquare className="w-4 h-4 mr-2" />
              Help Requests ({filteredRequests.length})
            </TabsTrigger>
          </TabsList>

          <TabsContent value="offers">
            {loading ? (
              <div className="flex justify-center py-12">
                <Loader2 className="w-8 h-8 animate-spin text-[#ffd703]" />
              </div>
            ) : filteredOffers.length === 0 ? (
              <Card className="">
                <CardContent className="py-12 text-center">
                  <HandHeart className="w-12 h-12 mx-auto text-[#d1c7e9] mb-4" />
                  <h3 className="font-medium text-[#090000] mb-2">No skill offers found</h3>
                  <p className="text-sm text-[#090000/60] mb-4">Be the first to offer your skills!</p>
                  <Button onClick={() => setShowOfferDialog(true)} className="bg-[#ffd703] hover:bg-[#e6c203]">
                    <Plus className="w-4 h-4 mr-2" />
                    Post an Offer
                  </Button>
                </CardContent>
              </Card>
            ) : (
              <div className="grid md:grid-cols-2 lg:grid-cols-3 gap-6">
                {filteredOffers.map((offer) => (
                  <Card key={offer.offer_id} className="hover: transition-shadow cursor-pointer" onClick={() => setSelectedPost({ kind: 'offer', data: offer })} data-testid={`offer-card-${offer.offer_id}`}>
                    <CardContent className="p-6">
                      <div className="flex items-start justify-between mb-4 flex-wrap gap-2">
                        <Badge className="bg-[#ffd703] text-black">{offer.skill_category}</Badge>
                        <Badge className="bg-[#c7dfe9] text-black" data-testid={`offer-region-${offer.offer_id}`}>{getRegionLabel(offer.region)}</Badge>
                        <span className="flex items-center gap-1 text-sm text-[#090000/60]">
                          <Clock className="w-4 h-4" />
                          {offer.estimated_hours}h
                        </span>
                      </div>
                      <h3 className="font-medium text-[#090000] mb-2">{offer.title}</h3>
                      <p className="text-sm text-[#090000/60] mb-4 line-clamp-3">{offer.description}</p>
                      <div className="flex items-center justify-between pt-4 border-t">
                        <div className="flex items-center gap-2">
                          <Avatar className="w-8 h-8">
                            <AvatarImage src={offer.user_picture} />
                            <AvatarFallback className="bg-[#d1c7e9] text-white text-xs">
                              {offer.user_name?.charAt(0)}
                            </AvatarFallback>
                          </Avatar>
                          <div className="flex flex-col">
                            <span className="text-sm text-[#090000] flex items-center gap-1">
                              <Link
                                to={`/users/${offer.user_id}`}
                                onClick={(e) => e.stopPropagation()}
                                className="hover:underline font-semibold"
                                data-testid={`offer-user-link-${offer.offer_id}`}
                              >
                                {offer.user_account_type === 'business' && offer.user_business_name ? offer.user_business_name : offer.user_name}
                              </Link>
                              {offer.user_account_type === 'business' && (
                                <Badge className="bg-[#d8b4fe] text-black text-[10px] px-1.5 py-0" data-testid={`offer-biz-${offer.offer_id}`}>
                                  <Building2 className="w-3 h-3 mr-0.5" /> Business
                                </Badge>
                              )}
                            </span>
                            {offer.user_account_type === 'business' && offer.user_business_name && (
                              <span className="text-xs text-black/50">by {offer.user_name}</span>
                            )}
                          </div>
                        </div>
                        <div className="flex items-center gap-2">
                          {React.createElement(getContactIcon(offer.contact_preference), {
                            className: "w-4 h-4 text-[#090000/60]"
                          })}
                          {offer.user_id === user?.user_id && (
                            <Button 
                              variant="ghost" 
                              size="sm"
                              onClick={(e) => { e.stopPropagation(); handleDeleteOffer(offer.offer_id); }}
                              className="text-[#ef4444] hover:text-[#ef4444] hover:bg-[#ef4444]/10"
                              data-testid={`delete-offer-${offer.offer_id}`}
                            >
                              Delete
                            </Button>
                          )}
                        </div>
                      </div>
                    </CardContent>
                  </Card>
                ))}
              </div>
            )}
          </TabsContent>

          <TabsContent value="requests">
            {loading ? (
              <div className="flex justify-center py-12">
                <Loader2 className="w-8 h-8 animate-spin text-[#ffd703]" />
              </div>
            ) : filteredRequests.length === 0 ? (
              <Card className="">
                <CardContent className="py-12 text-center">
                  <MessageSquare className="w-12 h-12 mx-auto text-[#d1c7e9] mb-4" />
                  <h3 className="font-medium text-[#090000] mb-2">No help requests found</h3>
                  <p className="text-sm text-[#090000/60] mb-4">Need help? Post a request!</p>
                  <Button onClick={() => setShowRequestDialog(true)} className="bg-[#ffd703] hover:bg-[#e6c203]">
                    <Plus className="w-4 h-4 mr-2" />
                    Post a Request
                  </Button>
                </CardContent>
              </Card>
            ) : (
              <div className="grid md:grid-cols-2 lg:grid-cols-3 gap-6">
                {filteredRequests.map((request) => (
                  <Card key={request.request_id} className="hover: transition-shadow cursor-pointer" onClick={() => setSelectedPost({ kind: 'request', data: request })} data-testid={`request-card-${request.request_id}`}>
                    <CardContent className="p-6">
                      <div className="flex items-start justify-between mb-4 flex-wrap gap-2">
                        <Badge className="bg-[#d8b4fe] text-black">{request.skill_needed}</Badge>
                        <Badge className="bg-[#c7dfe9] text-black" data-testid={`request-region-${request.request_id}`}>{getRegionLabel(request.region)}</Badge>
                        <Badge className={getUrgencyBadge(request.urgency)}>{request.urgency}</Badge>
                      </div>
                      <h3 className="font-medium text-[#090000] mb-2">{request.title}</h3>
                      <p className="text-sm text-[#090000/60] mb-4 line-clamp-3">{request.description}</p>
                      <div className="flex items-center gap-2 text-sm text-[#090000/60] mb-4">
                        <Clock className="w-4 h-4" />
                        Estimated: {request.estimated_hours} hours
                      </div>
                      <div className="flex items-center justify-between pt-4 border-t">
                        <div className="flex items-center gap-2">
                          <Avatar className="w-8 h-8">
                            <AvatarImage src={request.user_picture} />
                            <AvatarFallback className="bg-[#d1c7e9] text-white text-xs">
                              {request.user_name?.charAt(0)}
                            </AvatarFallback>
                          </Avatar>
                          <div className="flex flex-col">
                            <span className="text-sm text-[#090000] flex items-center gap-1">
                              <Link
                                to={`/users/${request.user_id}`}
                                onClick={(e) => e.stopPropagation()}
                                className="hover:underline font-semibold"
                                data-testid={`request-user-link-${request.request_id}`}
                              >
                                {request.user_account_type === 'business' && request.user_business_name ? request.user_business_name : request.user_name}
                              </Link>
                              {request.user_account_type === 'business' && (
                                <Badge className="bg-[#d8b4fe] text-black text-[10px] px-1.5 py-0" data-testid={`request-biz-${request.request_id}`}>
                                  <Building2 className="w-3 h-3 mr-0.5" /> Business
                                </Badge>
                              )}
                            </span>
                            {request.user_account_type === 'business' && request.user_business_name && (
                              <span className="text-xs text-black/50">by {request.user_name}</span>
                            )}
                          </div>
                        </div>
                        {request.user_id === user?.user_id && (
                          <Button 
                            variant="ghost" 
                            size="sm"
                            onClick={(e) => { e.stopPropagation(); handleDeleteRequest(request.request_id); }}
                            className="text-[#ef4444] hover:text-[#ef4444] hover:bg-[#ef4444]/10"
                            data-testid={`delete-request-${request.request_id}`}
                          >
                            Delete
                          </Button>
                        )}
                      </div>
                    </CardContent>
                  </Card>
                ))}
              </div>
            )}
          </TabsContent>
        </Tabs>

        {/* Post detail dialog */}
        <Dialog open={!!selectedPost} onOpenChange={(o) => { if (!o) setSelectedPost(null); }}>
          <DialogContent className="max-w-lg" data-testid="post-detail-dialog">
            {selectedPost && (() => {
              const isOffer = selectedPost.kind === 'offer';
              const p = selectedPost.data;
              const category = isOffer ? p.skill_category : p.skill_needed;
              const authorLabel = p.user_account_type === 'business' && p.user_business_name ? p.user_business_name : p.user_name;
              const contactHref = p.contact_preference === 'email' && p.contact_value?.includes('@')
                ? `mailto:${p.contact_value}`
                : p.contact_preference === 'phone' || p.contact_preference === 'whatsapp'
                  ? `tel:${p.contact_value?.replace(/[^0-9+]/g,'')}`
                  : null;
              return (
                <>
                  <DialogHeader>
                    <DialogTitle className="flex items-center gap-2 flex-wrap">
                      {isOffer ? 'Skill Offer' : 'Help Request'}
                      <Badge className="bg-[#ffd703] text-black">{category}</Badge>
                    </DialogTitle>
                    <DialogDescription>Posted by {authorLabel} in {getRegionLabel(p.region)}</DialogDescription>
                  </DialogHeader>
                  <div className="space-y-4 mt-2">
                    <h3 className="text-lg font-bold text-black" data-testid="detail-title">{p.title}</h3>
                    <p className="text-black font-sans whitespace-pre-wrap" data-testid="detail-description">{p.description}</p>

                    <div className="flex flex-wrap gap-2 text-xs font-sans">
                      <Badge variant="outline" className="bg-white">
                        <Clock className="w-3 h-3 mr-1" /> ~{isOffer ? p.estimated_hours : p.estimated_hours}h
                      </Badge>
                      {!isOffer && p.urgency && (
                        <Badge className={p.urgency === 'high' ? 'bg-red-200 text-black' : p.urgency === 'medium' ? 'bg-[#ffd703] text-black' : 'bg-green-200 text-black'}>
                          urgency: {p.urgency}
                        </Badge>
                      )}
                      {p.user_account_type === 'business' && (
                        <Badge className="bg-[#d8b4fe] text-black"><Building2 className="w-3 h-3 mr-1" /> Business</Badge>
                      )}
                    </div>

                    <div className="rounded-md border-2 border-black bg-[#fef9e7] p-4 space-y-2">
                      <p className="text-xs font-sans font-semibold text-black/60">CONTACT</p>
                      {p.contact_value ? (
                        <div className="flex items-center gap-2 flex-wrap">
                          <span className="text-xs text-black/70 uppercase font-semibold">{p.contact_preference}</span>
                          {contactHref ? (
                            <a href={contactHref} className="font-sans font-bold text-black underline" data-testid="detail-contact-link">{p.contact_value}</a>
                          ) : (
                            <span className="font-sans font-bold text-black" data-testid="detail-contact">{p.contact_value}</span>
                          )}
                        </div>
                      ) : (
                        <p className="text-sm text-black/60 italic">No contact info attached to this post.</p>
                      )}
                    </div>

                    <div className="pt-2 border-t-2 border-black flex flex-wrap gap-3 items-center justify-between">
                      <Link
                        to={`/users/${p.user_id}`}
                        onClick={() => setSelectedPost(null)}
                        className="inline-flex items-center gap-2 text-sm font-sans font-semibold text-black underline"
                        data-testid="detail-view-profile"
                      >
                        <User className="w-4 h-4" /> View {authorLabel}'s profile →
                      </Link>
                      {p.user_id !== user?.user_id && (
                        <Link
                          to={`/exchanges?log=1&partner_id=${encodeURIComponent(p.user_id)}&hours=${encodeURIComponent(p.estimated_hours || 1)}&description=${encodeURIComponent(p.title || '')}&contact_method=${encodeURIComponent(p.contact_preference || 'email')}&type=${isOffer ? 'received' : 'gave'}`}
                          onClick={() => setSelectedPost(null)}
                          className="inline-flex items-center gap-2 rounded-lg border-[3px] border-black bg-[#ffd703] px-4 py-2 text-sm font-sans font-semibold shadow-[4px_4px_0px_#000] hover:translate-x-[2px] hover:translate-y-[2px] hover:shadow-[2px_2px_0px_#000] transition-all"
                          data-testid="detail-log-exchange"
                        >
                          <Clock className="w-4 h-4" /> Log this exchange
                        </Link>
                      )}
                    </div>
                  </div>
                </>
              );
            })()}
          </DialogContent>
        </Dialog>
      </main>
    </div>
  );
}
